import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR } from './lib';
import { AlternativePicker, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock } from './SharedBlocks';

const PitchLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied,
  } = ctx;

  const cust = quote.customerData || {};
  const card = { background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '22px 24px' };

  const ServiceCard = ({ service, color, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ ...card, padding: '16px 18px', opacity: isOpt && !on ? 0.6 : 1, borderLeft: `4px solid ${color}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isOpt && (
                <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)}
                  style={{ width: 18, height: 18, accentColor: theme.primary, cursor: 'pointer' }} />
              )}
              <p style={{ fontWeight: 600, fontSize: 15, color: theme.textPrimary }}>{service.name}</p>
              {isOpt && <span style={{ fontSize: 11, background: `${theme.accent}22`, color: theme.accent, padding: '2px 8px', borderRadius: 99, fontWeight: 500 }}>Optional</span>}
            </div>
            {service.description && <p style={{ fontSize: 13, color: theme.textSecondary, marginTop: 5, lineHeight: 1.6 }}>{service.description}</p>}
            {service.customDescription && <p style={{ fontSize: 12, color: theme.textMuted, fontStyle: 'italic', marginTop: 3 }}>{service.customDescription}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 17, fontWeight: 700, color }}>{formatEUR(lineTotal(service))}</span>
            {suffix && <span style={{ fontSize: 12, color: theme.textSecondary }}> {suffix}</span>}
          </div>
        </div>
      </div>
    );
  };

  const Group = ({ title, services, totals, color, suffix }) => {
    if (!services.length) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color }}>{title}</h3>
        {services.map((s, i) => <ServiceCard key={s.id || i} service={s} color={color} suffix={suffix} />)}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 10, padding: '4px 4px 8px' }}>
          <span style={{ fontSize: 13, color: theme.textSecondary }}>Summe inkl. MwSt.</span>
          <span style={{ fontSize: 20, fontWeight: 700, color }}>{formatEUR(totals.brutto)}{suffix ? ` ${suffix}` : ''}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 48px' }}>
      {/* Action bar */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 20px', maxWidth: 880, margin: '0 auto' }}>
        <button onClick={handleSaveAsPdf} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: theme.radius, background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, color: theme.textPrimary, cursor: 'pointer', fontSize: 13 }}>
          <Download className="w-4 h-4" />PDF
        </button>
        <button onClick={handleCopyLink} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: theme.radius, background: theme.surface, border: `1px solid ${theme.surfaceBorder}`, color: theme.textPrimary, cursor: 'pointer', fontSize: 13 }}>
          {linkCopied ? <Check className="w-4 h-4" style={{ color: '#22c55e' }} /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}
        </button>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* HERO */}
        <div style={{ background: `linear-gradient(135deg, ${theme.primary}18, ${theme.accent}10)`, border: `1px solid ${theme.surfaceBorder}`, borderRadius: theme.radius, padding: '38px 32px' }}>
          {company?.logo && <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="Logo" style={{ height: 40, marginBottom: 22, objectFit: 'contain' }} />}
          {cust.company && <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: theme.primary, marginBottom: 10 }}>Für {cust.company}</p>}
          <h1 style={{ fontSize: 30, lineHeight: 1.2, fontWeight: 700, color: theme.textPrimary, margin: 0, maxWidth: 620 }}>
            {quote.heroHeadline || quote.quoteTitle || 'Ihr maßgeschneidertes Angebot.'}
          </h1>
          {quote.quoteIntro && <p style={{ fontSize: 15, color: theme.textSecondary, marginTop: 14, lineHeight: 1.7, maxWidth: 600, whiteSpace: 'pre-wrap' }}>{quote.quoteIntro}</p>}
          <div style={{ display: 'flex', gap: 20, marginTop: 22, flexWrap: 'wrap' }}>
            <div><span style={{ fontSize: 12, color: theme.textMuted }}>Angebot</span><br /><span style={{ fontWeight: 600 }}>{quote.quoteNumber}</span></div>
            <div><span style={{ fontSize: 12, color: theme.textMuted }}>Datum</span><br /><span style={{ fontWeight: 600 }}>{new Date(quote.createdAt).toLocaleDateString('de-DE')}</span></div>
          </div>
        </div>

        {/* Video */}
        {quote.videoUrl && (
          <div className="no-print" style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Persönliche Video-Präsentation</h3>
            <video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius, maxHeight: 420 }} />
          </div>
        )}

        {/* Arguments */}
        {quote.customerArguments && (
          <div style={{ ...card, borderLeft: `4px solid ${theme.primary}` }}>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: theme.primary, marginBottom: 10 }}>Warum dieses Angebot für Sie?</h3>
            <div style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}

        {/* Additional info */}
        {quote.additionalInfo && (
          <div style={card}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Weitere Informationen</h3>
            <p style={{ fontSize: 14, color: theme.textSecondary, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{quote.additionalInfo}</p>
          </div>
        )}

        {/* Services */}
        <Group title="Einmalige Leistungen" services={einmaligServices} totals={einmaligTotals} color={theme.einmalig} suffix="" />
        <Group title="Monatliche Leistungen" services={monatlichServices} totals={monatlichTotals} color={theme.monatlich} suffix="/ Monat" />
        <Group title="Jährliche Leistungen" services={jaehrlichServices} totals={jaehrlichTotals} color={theme.jaehrlich} suffix="/ Jahr" />

        {/* Grand investment summary */}
        <div style={{ background: theme.primary, borderRadius: theme.radius, padding: '26px 28px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <p style={{ fontSize: 13, opacity: .85, margin: 0 }}>Ihre Investition</p>
            <p style={{ fontSize: 28, fontWeight: 700, margin: '2px 0 0' }}>{formatEUR(einmaligTotals.brutto)}</p>
            {(monatlichTotals.brutto > 0 || jaehrlichTotals.brutto > 0) && (
              <p style={{ fontSize: 13, opacity: .9, marginTop: 4 }}>
                {monatlichTotals.brutto > 0 && <>+ {formatEUR(monatlichTotals.brutto)} / Monat </>}
                {jaehrlichTotals.brutto > 0 && <>+ {formatEUR(jaehrlichTotals.brutto)} / Jahr</>}
              </p>
            )}
          </div>
          <span style={{ fontSize: 12, opacity: .8 }}>inkl. 19% MwSt.</span>
        </div>

        <TermsBlock quote={quote} company={company} theme={theme} />
        <AcceptanceBlock ctx={ctx} theme={theme} />
        <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FeedbackBlock viewHash={viewHash} theme={theme} />
          <QuestionBlock viewHash={viewHash} theme={theme} />
        </div>
        <FooterBlock company={company} theme={theme} />
      </div>
    </div>
  );
};

export default PitchLayout;
